// Make Dawn's MediaGallery.setActiveMedia null-safe. When the variant gallery
// override is active, the live gallery's slides use synthesized data-media-ids
// (`{section.id}-vg-N`), so Dawn's lookup by `{section.id}-{variant.featured_media.id}`
// returns null and crashes on `classList.add`. We swap the gallery DOM in the
// variantChange handler below; this patch just stops the crash in between.
if (typeof customElements !== 'undefined' && customElements.whenDefined) {
  customElements.whenDefined('media-gallery').then(() => {
    const ctor = customElements.get('media-gallery');
    if (!ctor || ctor.prototype.__variantGallerySafePatched) return;
    const original = ctor.prototype.setActiveMedia;
    ctor.prototype.setActiveMedia = function (mediaId, prepend) {
      const viewer = this.elements && this.elements.viewer;
      if (!viewer || !viewer.querySelector(`[data-media-id="${mediaId}"]`)) return;
      return original.call(this, mediaId, prepend);
    };
    ctor.prototype.__variantGallerySafePatched = true;
  });

  // Same null-safety story for VariantSelects.updateMedia: when the modal's
  // slides use `-vg-N` ids, `modalContent.querySelector([data-media-id=<featured>])`
  // returns null and `modalContent.prepend(null)` throws. Skip the prepend
  // when the matching modal node doesn't exist.
  customElements.whenDefined('variant-selects').then(() => {
    const ctor = customElements.get('variant-selects');
    if (!ctor || ctor.prototype.__variantGallerySafePatched) return;
    ctor.prototype.updateMedia = function () {
      if (!this.currentVariant) return;
      if (!this.currentVariant.featured_media) return;

      const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
      mediaGalleries.forEach((mediaGallery) =>
        mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true)
      );

      const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
      if (!modalContent) return;
      const newMediaModal = modalContent.querySelector(
        `[data-media-id="${this.currentVariant.featured_media.id}"]`
      );
      if (!newMediaModal) return;
      modalContent.prepend(newMediaModal);
    };
    ctor.prototype.__variantGallerySafePatched = true;
  });
}

if (!customElements.get('variant-gallery-swap')) {
  customElements.define(
    'variant-gallery-swap',
    class VariantGallerySwap extends HTMLElement {
      constructor() {
        super();
        this.unsubscribe = null;
      }

      connectedCallback() {
        const sectionId = this.dataset.section;
        if (!sectionId || typeof subscribe !== 'function') return;

        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event || !event.data || event.data.sectionId !== sectionId) return;
          const html = event.data.html;
          if (!html) return;

          this.swap(html, `MediaGallery-${sectionId}`);
          this.swap(html, `MediaGallery-${sectionId}-duplicate`);
          this.swapModalContent(html, `ProductModal-${sectionId}`);
          this.rebindHoverZoom();
        });
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      swap(html, galleryId) {
        const next = html.getElementById(galleryId);
        const live = document.getElementById(galleryId);
        if (!next || !live) return;
        live.innerHTML = next.innerHTML;
      }

      // Swap modal slides so click-to-zoom uses the new variant's images.
      // We only replace the inner .product-media-modal__content; the dialog
      // chrome (close button, container) is preserved so product-modal.js
      // keeps working.
      swapModalContent(html, modalId) {
        const nextModal = html.getElementById(modalId);
        const liveModal = document.getElementById(modalId);
        if (!nextModal || !liveModal) return;
        const nextContent = nextModal.querySelector('.product-media-modal__content');
        const liveContent = liveModal.querySelector('.product-media-modal__content');
        if (!nextContent || !liveContent) return;
        liveContent.innerHTML = nextContent.innerHTML;
      }

      // magnify.js binds hover-zoom once at script load on .image-magnify-hover
      // elements. After we replace the gallery DOM, the new imgs have no
      // handler. Rebind by replicating magnify.js's enableZoomOnHover for the
      // new images. `magnify` and `moveWithHover` are top-level globals.
      rebindHoverZoom() {
        if (typeof magnify !== 'function' || typeof moveWithHover !== 'function') return;
        document.querySelectorAll('.image-magnify-hover').forEach((image) => {
          image.onclick = (event) => {
            magnify(image, 2);
            moveWithHover(image, event, 2);
          };
        });
      }
    }
  );
}
